import { useEffect, useState } from 'react';
import { AlertTriangle, Clock, X, Zap, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import useCcmLicenseStore from '../stores/ccmLicenseStore';

const CcmLicenseBanner = () => {
  const navigate = useNavigate();
  const { tenantVersion } = useAuthStore();
  const { license, fetchLicense } = useCcmLicenseStore();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchLicense();
  }, [tenantVersion, fetchLicense]);

  useEffect(() => {
    setDismissed(false);
  }, [tenantVersion]);

  if (!license || dismissed) return null;

  const goToUpgrade = () => window.open('https://serverliccm.com/contact', '_blank');

  // No license at all
  if (!license.hasLicense) {
    return (
      <div className="md-license-banner no-license" data-upgrade="ccm">
        <div className="md-license-banner-content">
          <Shield size={18} />
          <span>{license.message || 'No CCM license found for this site. Contact your administrator.'}</span>
        </div>
      </div>
    );
  }

  // Expired license (trial or paid) - red error banner
  if (license.isExpired) {
    const msg = license.isTrial
      ? 'Your free trial has expired. Upgrade to Pro for uninterrupted service.'
      : 'Your license has expired. Your site is in read-only mode. Upgrade to Pro for full access.';
    return (
      <div className="md-license-banner expired" data-upgrade="ccm">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>{msg}</span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn upgrade" onClick={goToUpgrade}>
            <Zap size={14} /> Upgrade
          </button>
        </div>
      </div>
    );
  }

  // Trial active
  if (license.isTrial && typeof license.daysRemaining === 'number') {
    const urgent = license.daysRemaining <= 3;
    const msg = urgent
      ? `Your free trial expires in ${license.daysRemaining} day${license.daysRemaining !== 1 ? 's' : ''}. Upgrade now to avoid losing access.`
      : `You have ${license.daysRemaining} days remaining on your 14-day free trial. Upgrade to Pro for uninterrupted service.`;
    return (
      <div className={`md-license-banner trial ${urgent ? 'urgent' : ''}`} data-upgrade="ccm">
        <div className="md-license-banner-content">
          <Clock size={18} />
          <span>{msg}</span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn upgrade" onClick={goToUpgrade}>
            <Zap size={14} /> {urgent ? 'Upgrade Now' : 'Upgrade'}
          </button>
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Over limit
  if (license.isOverLimit) {
    const which = license.currentEndpoints > license.maxEndpoints
      ? 'endpoint'
      : license.currentUsers > license.maxUsers
        ? 'user'
        : 'integration';
    return (
      <div className="md-license-banner expired" data-upgrade="ccm">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>You have exceeded your {which} limit. Upgrade your plan to continue adding resources.</span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn upgrade" onClick={goToUpgrade}>
            <Zap size={14} /> Upgrade
          </button>
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Near limit
  if (license.isNearLimit) {
    const nearest = license.currentEndpoints / license.maxEndpoints >= 0.8
      ? { type: 'endpoints', cur: license.currentEndpoints, max: license.maxEndpoints }
      : license.currentUsers / license.maxUsers >= 0.8
        ? { type: 'users', cur: license.currentUsers, max: license.maxUsers }
        : { type: 'integrations', cur: license.currentIntegrations, max: license.maxIntegrations };
    return (
      <div className="md-license-banner trial" data-upgrade="ccm">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>You are using {nearest.cur} of {nearest.max} {nearest.type}.</span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn upgrade" onClick={goToUpgrade}>
            <Zap size={14} /> Upgrade
          </button>
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ACTIVE paid - no banner
  return null;
};

export default CcmLicenseBanner;
